import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Modal from '../components/Modal';
import { Plus, Edit3, Trash2, Minus, Droplets, ArrowLeft, Check, Palette, PenTool } from 'lucide-react';

const MATERIALS = ['PLA', 'PLA Basic', 'PLA Matte', 'PETG', 'ABS', 'ASA', 'TPU', 'Nylon', 'PC', 'PVA', 'HIPS', 'Carbon Fiber', 'Wood Fill', 'Resin - Standard', 'Resin - ABS-Like', 'Resin - Tough', 'Resin - Flexible'];

export default function FilamentsPage() {
  const [filaments, setFilaments] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addStep, setAddStep] = useState('pick'); // 'pick' | 'colors' | 'custom'
  const [selectedLine, setSelectedLine] = useState(null); // { brand, line, colors }
  const [selectedColors, setSelectedColors] = useState(new Set());
  const [presets, setPresets] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showEdit, setShowEdit] = useState(null);
  const [showDeduct, setShowDeduct] = useState(null);
  const [deductAmount, setDeductAmount] = useState(0);
  const [form, setForm] = useState({ brand: '', material: 'PLA', color: '', color_hex: '#6366f1', diameter: 1.75, weight_total: 1000, weight_remaining: 1000, cost_per_kg: '', notes: '' });

  const loadFilaments = () => api.get('/filaments').then(r => setFilaments(r.data.filaments));
  useEffect(() => { loadFilaments(); }, []);

  // Load presets when modal opens
  useEffect(() => {
    if (showAddModal && !presets) {
      api.get('/filaments/presets').then(r => setPresets(r.data.presets)).catch(() => {});
    }
  }, [showAddModal, presets]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const openAddModal = () => {
    setAddStep('pick');
    setSelectedLine(null);
    setSelectedColors(new Set());
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddStep('pick');
    setSelectedLine(null);
    setSelectedColors(new Set());
  };

  const pickLine = (brand, lineName, colors) => {
    setSelectedLine({ brand, line: lineName, colors });
    setSelectedColors(new Set());
    setAddStep('colors');
  };

  const toggleColor = (idx) => {
    setSelectedColors(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleAddPresets = async () => {
    if (selectedColors.size === 0 || !selectedLine) return;
    setSaving(true);
    try {
      const promises = [...selectedColors].map(idx => {
        const preset = selectedLine.colors[idx];
        return api.post('/filaments', preset);
      });
      await Promise.all(promises);
      showToast(`${selectedColors.size} filament${selectedColors.size > 1 ? 's' : ''} added`);
      closeAddModal();
      loadFilaments();
    } catch (err) {
      showToast('Error adding filaments');
    } finally {
      setSaving(false);
    }
  };

  const openCustom = () => {
    resetForm();
    setAddStep('custom');
  };

  const handleCreate = async () => {
    if (!form.material) return;
    await api.post('/filaments', { ...form, cost_per_kg: form.cost_per_kg ? parseFloat(form.cost_per_kg) : null });
    closeAddModal();
    resetForm();
    loadFilaments();
    showToast('Filament added');
  };

  const handleUpdate = async () => {
    await api.put(`/filaments/${showEdit}`, { ...form, cost_per_kg: form.cost_per_kg ? parseFloat(form.cost_per_kg) : null });
    setShowEdit(null);
    loadFilaments();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this filament?')) return;
    await api.delete(`/filaments/${id}`);
    loadFilaments();
  };

  const handleDeduct = async () => {
    if (!deductAmount || deductAmount <= 0) return;
    await api.post(`/filaments/${showDeduct}/deduct`, { grams: deductAmount });
    setShowDeduct(null);
    setDeductAmount(0);
    loadFilaments();
  };

  const resetForm = () => setForm({ brand: '', material: 'PLA', color: '', color_hex: '#6366f1', diameter: 1.75, weight_total: 1000, weight_remaining: 1000, cost_per_kg: '', notes: '' });

  const openEdit = (f) => {
    setForm({ brand: f.brand, material: f.material, color: f.color, color_hex: f.color_hex, diameter: f.diameter, weight_total: f.weight_total, weight_remaining: f.weight_remaining, cost_per_kg: f.cost_per_kg || '', notes: f.notes || '' });
    setShowEdit(f.id);
  };

  // Build swatch strip for a line
  const SwatchStrip = ({ colors, muted }) => (
    <div style={{ display: 'flex', gap: '2px', marginTop: '8px' }}>
      {colors.slice(0, 15).map((c, i) => (
        <div key={i} style={{
          width: '14px', height: '14px', borderRadius: '50%',
          backgroundColor: c.color_hex,
          border: '1px solid rgba(255,255,255,0.15)',
          opacity: muted ? 0.75 : 1,
        }} />
      ))}
      {colors.length > 15 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', alignSelf: 'center', marginLeft: '4px' }}>+{colors.length - 15}</span>}
    </div>
  );

  // Determine if color is dark for contrast
  const isDark = (hex) => {
    const r = parseInt(hex.slice(1,3), 16);
    const g = parseInt(hex.slice(3,5), 16);
    const b = parseInt(hex.slice(5,7), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Filament Inventory</h1>
        <button className="btn btn-primary" onClick={openAddModal}>
          <Plus size={16} /> Add Filament
        </button>
      </div>

      {filaments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
          <Droplets size={56} style={{ marginBottom: '16px', opacity: 0.2 }} />
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>No filaments tracked</p>
          <p style={{ fontSize: '14px' }}>Add your filament spools to track inventory</p>
        </div>
      ) : (
        <div className="grid grid-3">
          {filaments.map(f => {
            const pct = f.weight_total > 0 ? Math.round((f.weight_remaining / f.weight_total) * 100) : 0;
            const isLow = pct < 20;
            return (
              <div key={f.id} className="card">
                <div className="card-body">
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'start' }}>
                    <div className="filament-swatch" style={{ backgroundColor: f.color_hex, borderColor: f.color_hex + '88' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--accent)', textTransform: 'uppercase' }}>{f.brand || 'Generic'}</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{f.material}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{f.color || 'Unspecified'} · {f.diameter}mm</div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn-icon" style={{ padding: '5px' }} onClick={() => openEdit(f)}><Edit3 size={13} /></button>
                      <button className="btn-icon" style={{ padding: '5px' }} onClick={() => handleDelete(f.id)}><Trash2 size={13} /></button>
                    </div>
                  </div>

                  <div style={{ marginTop: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: isLow ? 'var(--warning)' : 'var(--text-secondary)' }}>
                        {isLow ? '⚠️ Low stock' : 'Remaining'}
                      </span>
                      <span style={{ fontWeight: '600' }}>{Math.round(f.weight_remaining)}g / {f.weight_total}g</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{
                        width: `${pct}%`,
                        background: isLow ? 'var(--warning)' : pct > 50 ? 'var(--success)' : 'var(--accent)'
                      }} />
                    </div>
                  </div>

                  {f.cost_per_kg && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                      ${f.cost_per_kg}/kg · ~${((f.cost_per_kg / 1000) * f.weight_remaining).toFixed(2)} remaining value
                    </div>
                  )}

                  <button className="btn btn-secondary btn-sm" style={{ marginTop: '12px', width: '100%', justifyContent: 'center' }}
                    onClick={() => { setShowDeduct(f.id); setDeductAmount(0); }}>
                    <Minus size={13} /> Deduct Usage
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Filament Modal — Picker / Colors / Custom */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title={addStep === 'colors' ? `${selectedLine?.brand} ${selectedLine?.line}` : 'Add Filament'}
        wide={addStep === 'colors'}
        footer={
          addStep === 'colors' ? (
            <>
              <button className="btn btn-secondary" onClick={() => setAddStep('pick')}><ArrowLeft size={14} /> Back</button>
              <button className="btn btn-primary" onClick={handleAddPresets} disabled={selectedColors.size === 0 || saving}>
                {saving ? 'Adding...' : `Add ${selectedColors.size} selected`}
              </button>
            </>
          ) : addStep === 'custom' ? (
            <>
              <button className="btn btn-secondary" onClick={() => setAddStep('pick')}><ArrowLeft size={14} /> Back</button>
              <button className="btn btn-primary" onClick={handleCreate}>Add</button>
            </>
          ) : null
        }
      >
        {addStep === 'pick' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {presets && Object.entries(presets).map(([brand, lines]) =>
              Object.entries(lines).map(([lineName, colors]) => (
                <button key={`${brand}-${lineName}`}
                  onClick={() => pickLine(brand, lineName, colors)}
                  style={{
                    background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '16px 20px', cursor: 'pointer',
                    textAlign: 'left', transition: 'border-color 0.15s',
                  }}
                  onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Palette size={20} style={{ color: 'var(--accent)' }} />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text)' }}>{brand} {lineName}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{colors.length} colors · 1.75mm · 1kg spools</div>
                    </div>
                  </div>
                  <SwatchStrip colors={colors} muted={lineName.includes('Matte')} />
                </button>
              ))
            )}
            <button
              onClick={openCustom}
              style={{
                background: 'var(--surface-raised)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: '16px 20px', cursor: 'pointer',
                textAlign: 'left', transition: 'border-color 0.15s',
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <PenTool size={20} style={{ color: 'var(--text-muted)' }} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text)' }}>Custom</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Manually enter filament details</div>
                </div>
              </div>
            </button>
          </div>
        )}

        {addStep === 'colors' && selectedLine && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '12px' }}>
            {selectedLine.colors.map((c, idx) => {
              const isSelected = selectedColors.has(idx);
              const dark = isDark(c.color_hex);
              return (
                <button key={idx} onClick={() => toggleColor(idx)} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%',
                    backgroundColor: c.color_hex,
                    border: isSelected ? '3px solid var(--accent)' : '2px solid rgba(255,255,255,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border 0.15s, transform 0.15s',
                    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: isSelected ? '0 0 0 2px var(--accent)' : 'none',
                  }}>
                    {isSelected && <Check size={18} color={dark ? '#fff' : '#000'} strokeWidth={3} />}
                  </div>
                  <span style={{
                    fontSize: '10px', color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
                    fontWeight: isSelected ? '600' : '400', textAlign: 'center', lineHeight: '1.2',
                    maxWidth: '80px', wordBreak: 'break-word',
                  }}>{c.color}</span>
                </button>
              );
            })}
          </div>
        )}

        {addStep === 'custom' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>Brand</label>
                <input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Bambu Lab" />
              </div>
              <div className="form-group">
                <label>Material</label>
                <select value={form.material} onChange={e => setForm({ ...form, material: e.target.value })}>
                  {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px' }}>
              <div className="form-group">
                <label>Color Name</label>
                <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="e.g. Matte Black" />
              </div>
              <div className="form-group">
                <label>Color</label>
                <input type="color" value={form.color_hex} onChange={e => setForm({ ...form, color_hex: e.target.value })} style={{ width: '48px', height: '38px', padding: '2px' }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>Diameter (mm)</label>
                <input type="number" step="0.05" value={form.diameter} onChange={e => setForm({ ...form, diameter: parseFloat(e.target.value) })} />
              </div>
              <div className="form-group">
                <label>Total Weight (g)</label>
                <input type="number" value={form.weight_total} onChange={e => setForm({ ...form, weight_total: parseFloat(e.target.value) })} />
              </div>
              <div className="form-group">
                <label>Remaining (g)</label>
                <input type="number" value={form.weight_remaining} onChange={e => setForm({ ...form, weight_remaining: parseFloat(e.target.value) })} />
              </div>
            </div>
            <div className="form-group">
              <label>Cost per kg ($)</label>
              <input type="number" step="0.01" value={form.cost_per_kg} onChange={e => setForm({ ...form, cost_per_kg: e.target.value })} placeholder="Optional" />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional notes..." />
            </div>
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEdit !== null}
        onClose={() => setShowEdit(null)}
        title="Edit Filament"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowEdit(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleUpdate}>Save</button>
          </>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div className="form-group">
            <label>Brand</label>
            <input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Bambu Lab" />
          </div>
          <div className="form-group">
            <label>Material</label>
            <select value={form.material} onChange={e => setForm({ ...form, material: e.target.value })}>
              {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px' }}>
          <div className="form-group">
            <label>Color Name</label>
            <input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="e.g. Matte Black" />
          </div>
          <div className="form-group">
            <label>Color</label>
            <input type="color" value={form.color_hex} onChange={e => setForm({ ...form, color_hex: e.target.value })} style={{ width: '48px', height: '38px', padding: '2px' }} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div className="form-group">
            <label>Diameter (mm)</label>
            <input type="number" step="0.05" value={form.diameter} onChange={e => setForm({ ...form, diameter: parseFloat(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Total Weight (g)</label>
            <input type="number" value={form.weight_total} onChange={e => setForm({ ...form, weight_total: parseFloat(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Remaining (g)</label>
            <input type="number" value={form.weight_remaining} onChange={e => setForm({ ...form, weight_remaining: parseFloat(e.target.value) })} />
          </div>
        </div>
        <div className="form-group">
          <label>Cost per kg ($)</label>
          <input type="number" step="0.01" value={form.cost_per_kg} onChange={e => setForm({ ...form, cost_per_kg: e.target.value })} placeholder="Optional" />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional notes..." />
        </div>
      </Modal>

      {/* Deduct Modal */}
      <Modal isOpen={showDeduct !== null} onClose={() => setShowDeduct(null)} title="Deduct Filament Usage"
        footer={<><button className="btn btn-secondary" onClick={() => setShowDeduct(null)}>Cancel</button><button className="btn btn-primary" onClick={handleDeduct}>Deduct</button></>}>
        <div className="form-group">
          <label>Amount to deduct (grams)</label>
          <input type="number" min="0" value={deductAmount} onChange={e => setDeductAmount(parseFloat(e.target.value) || 0)} autoFocus />
        </div>
      </Modal>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', background: 'var(--accent)',
          color: '#000', padding: '10px 20px', borderRadius: 'var(--radius)', fontWeight: '600',
          fontSize: '14px', zIndex: 10000, boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          animation: 'fadeIn 0.2s ease',
        }}>{toast}</div>
      )}
    </div>
  );
}
